import { useState, useEffect } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { validateGoogleIdToken } from '@Helpers/googleAuth/validateGoogleIdToken';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import bcrypt from 'bcryptjs'
import { ReactComponent as KaizIQLogoNoText } from '@Assets/kaiziqLogoNoText.svg';
import { ReactComponent as KaizIQLogoNoTextWhite } from '@Assets/kaiziqLogoNoTextWhite.svg';
import ProjectLinkService from '@Helpers/URLHelpers/ProjectLinkService';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import Alert from '@mui/material/Alert';
import { ReactComponent as Analytics } from '@Assets/analytics.svg';
import { ReactComponent as Boost } from '@Assets/boost.svg';
import { ReactComponent as Goal } from '@Assets/goal.svg';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { BsPerson as User } from 'react-icons/bs';
import { BiShow, BiHide } from 'react-icons/bi';
import { MdEmail, MdLock, MdPhone } from 'react-icons/md';
import { FcGoogle } from 'react-icons/fc';
import ErrorHandler from '@Root/ErrorHandler';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UserPreferenceService from '@Helpers/URLHelpers/UserPreferenceService';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import '@Partials/_ComStyles.scss';
import './LoginPage.scss';

const GOOGLE_CLIENT_ID = '704888664600-l1g177ip7drg2060329ievengm13o6j0.apps.googleusercontent.com';

function decodeJwt (token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error('Failed to decode Google credential', e);
        return {};
    }
}

function sanitizePhoneNumber(input) {
    // Remove leading +, spaces, dashes, commas
    return input.replace(/^[+]+/, '') // remove leading +
                .replace(/[\s-,]/g, ''); // remove spaces, dashes, commas
}


export default function LoginPage({ setToken, setEntryPage }) {

    const className = 'loginPage';
    const [username, setUserName] = useState(null);
    const [password, setPassword] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [cursor, setCursor] = useState('auto');
    const [invalidPassword, setInvalidPassword] = useState(false);
    const [manualLoginMethod, setManualLoginMethod] = useState(null);
    const [errorLoggingUser, setErrorLoggingUser] = useState(null);
    const [loginInvalid, setLoginInvalid] = useState(false);
    const [googleErrors, setGoogleErrors] = useState([]);
    const [isFading, setIsFading] = useState(false);
    const [preload, setPreload] = useState(true);
    const [loginStep, setLoginStep] = useState(1); // 1 = identifier entry, 2 = password/otp entry
    const [checkingLoginMethod, setCheckingLoginMethod] = useState(false); // loader while determining password vs otp
    const [otp, setOtp] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpResendCooldown, setOtpResendCooldown] = useState(0); // seconds
    const [otpError, setOtpError] = useState(null);
    const [conflictType, setConflictType] = useState(null);
    const [existingPlatform, setExistingPlatform] = useState(null);
    const [showInvalidLoginPopup, setShowInvalidLoginPopup] = useState(false);
    const [phoneError, setPhoneError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentLeads, setCurrentLeads, setLeadsList, setNumbersList } = useReduxLeads();
    const { setActiveUserJourney, setActiveUserPermission, setActiveUserAccessList, setActiveUser, setActiveUserPreferencesAll } = useReduxActiveUser();
    const { setCurrentOrg  } = useReduxOrg();
    const {  setCurrentProject } = useReduxProject();
    const userAuthService = new UserAuthService();
    const projectLinkService = new ProjectLinkService();
    const userPreferenceService = new UserPreferenceService();
    const urlEncryptionService = new UrlEncryptionService();
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const errorHandler = new ErrorHandler();

    const handleLogin = async (e, loginObject) => {
        e?.preventDefault();
        setCursor('wait');
        try {
            let activeUserPreferences, activeUserJourney, activeOrg, activeProject, accessList;
            const retToken = await userAuthService.login(loginObject);
            if (retToken?.token !== 'void') {
                setActiveUser(JSON.parse(retToken?.activeUser));
                setActiveUserPermission(JSON.parse(retToken?.activeUserPermissions));
                if(!!retToken?.activeUserPreferences){
                    activeUserPreferences = JSON.parse(retToken?.activeUserPreferences);
                    setActiveUserPreferencesAll(JSON.parse(retToken?.activeUserPreferences));
                } else {
                    setActiveUserPreferencesAll({});
                }
                if(!!retToken.activeUserJourney){
                    setActiveUserJourney(activeUserJourney);
                } else {
                    setActiveUserJourney({});
                }
                let userAccessList = await userAuthService.getAccessListForUser(retToken?.activeUserId);
                userAccessList = JSON.parse(userAccessList.body);
                setActiveUserAccessList(userAccessList);
                accessList = userAccessList;
                if(accessList?.orgs?.length > 0) {
                    let orgsList = accessList.orgs.filter((item)=> item.visibility === 'leads');
                    activeOrg = orgsList.at(-1);
                    if(!!activeOrg) {
                        setCurrentOrg(activeOrg);
                    } else {
                        setToken(retToken);
                        setEntryPage('signupSuccess');
                        navigate('/onboarding');
                        return;
                    }
                }
                if(accessList?.projects?.length > 0) {
                    let projects = !!accessList.projects ? accessList.projects : [];
                    let parentId = !!activeOrg.id ? activeOrg?.id : 0;
                    let getProjectsInOrg = projects.filter((item)=> (item.parentId === parentId && item.parentType === 'org') && item);
                    let projectList = getProjectsInOrg.filter((item)=> item.visibility === 'leads');
                    activeProject = projectList.at(-1);
                    if(!!activeProject) {
                        setCurrentProject(activeProject);
                        let response = await kaiziqLeadsConnectionService.getLeadsAndPhoneNumberListByProjectId(activeProject.id);
                        if(response.status < 400) {
                            response = JSON.parse(response.body);
                            let leadsList = response.leadsList;
                            let phoneNumbers = response.phoneNumberList;
                            setLeadsList(leadsList);
                            if(leadsList.length > 0) {
                                setCurrentLeads(leadsList[0]);
                                setNumbersList(phoneNumbers);
                            }
                        }
                    } else {
                        setToken(retToken);
                        setEntryPage('signupSuccess');
                        navigate('/onboarding');
                        return;
                    }
                }
            } else {
                setLoginInvalid(true);
                setConflictType(retToken?.conflictType || null);
                setExistingPlatform(retToken?.existingPlatform || null);
                setErrorLoggingUser(
                    !!retToken?.message ? retToken?.message :
                    (retToken?.conflictType === 'EXISTS_MANUAL_WHEN_SOCIAL'
                        ? 'Account exists with password login. Use your password or reset it.'
                        : retToken?.conflictType === 'EXISTS_OTHER_SOCIAL_PLATFORM'
                        ? `Account exists with a different social platform${retToken?.existingPlatform ? ` (${retToken?.existingPlatform})` : ''}.`
                        : retToken?.conflictType === 'EXISTS_SOCIAL_WHEN_MANUAL'
                        ? `Account exists with social login${retToken?.existingPlatform ? ` via ${retToken?.existingPlatform}` : ''}. Continue with social login.`
                        : 'We could not log you in. Please try again.'
                    )
                );
                setShowInvalidLoginPopup(true);
                if (retToken?.conflictType === 'EXISTS_SOCIAL_WHEN_MANUAL' || retToken?.conflictType === 'EXISTS_OTHER_SOCIAL_PLATFORM') {
                    setLoginStep(1);
                    setManualLoginMethod(null);
                }
            }
            if(!!retToken && retToken?.token !== 'void') {
                setToken(retToken);
                let journeyType = 'existing_user';
                if (retToken.activeUserJourney) {
                    try {
                        const journeyObj = typeof retToken.activeUserJourney === 'string'
                            ? JSON.parse(retToken.activeUserJourney)
                            : retToken.activeUserJourney;
                        journeyType = journeyObj?.status || 'existing_user';
                    } catch (e) {
                        journeyType = 'existing_user';
                    }
                }
                if (journeyType === 'new_user') {
                    setEntryPage('signupSuccess');
                    navigate('/onboarding');
                    return;
                } else {
                    navigate('/home', { replace: true });
                    return;
                }
            }
            if(!!retToken && retToken?.token == 'void') {
                SnackbarManager.show(
                    'Password is incorrect.',
                    'error'
                );
            }
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show(
                'Something went wrong. Please try again!',
                'error'
            );
        }
        setCursor('auto');
    }

    const handleGoogleSuccess = (credentialResponse) => {
        const rawCred = credentialResponse.credential || '';
        const { valid, errors, payload } = validateGoogleIdToken(rawCred, { expectedAud: GOOGLE_CLIENT_ID });
        setGoogleErrors(errors);
        if(!valid) return;
        const googleEmail = payload.email;
        const fullName = payload.name || (payload.given_name && payload.family_name ? payload.given_name + ' ' + payload.family_name : undefined);
        if(!googleEmail || !fullName) {
            setErrorLoggingUser('Google Sign-In Failed');
            return;
        }
        const loginObject = {
            entry: googleEmail,
            fullName: fullName,
            loginMethod: 'social',
            socialPlatform: 'google',
            sub: payload.sub
        };
        handleLogin(null, loginObject);
    };

    const handleSubmit = async e => {
        e?.preventDefault();
        const loginObject = {
            loginMethod: manualLoginMethod
        };
        if((manualLoginMethod === 'otp') || (manualLoginMethod === 'otpVerified')) {
            loginObject.otp = otp;
            loginObject.entry = username;
        }
        if((manualLoginMethod === 'password') || (manualLoginMethod === 'passwordVerified')) {
            loginObject.entry = username;
            loginObject.password = password;
        }
        handleLogin(e, loginObject);
    }

    const handleGoogleFailure = () => {
        setErrorLoggingUser('Google Sign-In Failed');
    };

    useEffect(() => {
        const t = setTimeout(() => setPreload(false), 20);
        return () => clearTimeout(t);
    }, []);

    const triggerFadeAnd = (actionCb) => {
        setIsFading(true);
        setTimeout(() => {
            actionCb();
        }, 300);
    };

    const signup = async e => {
        e?.preventDefault();
        triggerFadeAnd(() => setEntryPage('signup'));
    }

    const forgotPassword = async e => {
        e?.preventDefault();
        triggerFadeAnd(() => setEntryPage('forgotPassword'));
    }

    const handleUserNameChange = (e) => {
        let sanitizedUserName = DOMPurify.sanitize(e.target.value);
        setUserName(sanitizedUserName);

        const phoneRegex = /^(\+?\d[\d\s\-()]{7,})$/;
        if (phoneRegex.test(sanitizedUserName.trim())) {
            if (!sanitizedUserName.trim().startsWith('+')) {
                setPhoneError('Please enter your phone number with country code (e.g. +91XXXXXXXXXX)');
            } else {
                setPhoneError(null);
            }
        } else {
            setPhoneError(null);
        }
    }

    const handlePasswordChange = (e) => {
        let sanitizedPassword = DOMPurify.sanitize(e.target.value);
        if(sanitizedPassword.length === 0) {
            setInvalidPassword(true);
        } else {
            setInvalidPassword(false);
        }
        let salt = '$2a$10$CwTycUXWue0Thq9StjUM0u'
        let hashedPassword = bcrypt.hashSync(sanitizedPassword, salt)
        setPassword(hashedPassword);
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    }

    const openContactUsPage = () => {
        window.open('https://kaiziq.io/contact', '_blank', 'noopener,noreferrer');
    };

    const handlePasswordCheck = async (e) => {
        e?.preventDefault();
        if(!username || username.trim() === '') return;
        setErrorLoggingUser(null);
        setCheckingLoginMethod(true);
        setIsFading(true);
        setTimeout(async () => {
            try {
                let entryValue = username;
                const phoneRegex = /^(\+?\d[\d\s\-(),]{7,})$/;
                if (phoneRegex.test(username.trim())) {
                    entryValue = sanitizePhoneNumber(username.trim());
                }
                let body = {
                    'entry': entryValue
                };
                const resp = await userAuthService.getUserLoginMethod(body);
                if(resp && Object.prototype.hasOwnProperty.call(resp, 'loginMethod')) {
                    let loginMethod = resp.loginMethod;
                    if(loginMethod && (loginMethod === 'password' || loginMethod === 'otp' ||
                        loginMethod === 'passwordVerified' || loginMethod === 'otpVerified'   )) {
                        setManualLoginMethod(loginMethod);
                        setLoginStep(2);
                        if(loginMethod === 'otp' || loginMethod === 'otpVerified') {
                            sendOtp(username);
                        }
                    } else {
                        setManualLoginMethod(null);
                        setLoginStep(1);
                        setErrorLoggingUser('Invalid login details. Please check your details and try again.');
                        setShowInvalidLoginPopup(true);
                    }
                } else {
                    setManualLoginMethod(null);
                    setLoginStep(1);
                    setErrorLoggingUser('Could not determine login method (server issue). Please try again later.');
                }
            } catch(err) {
                setManualLoginMethod(null);
                setLoginStep(1);
                setErrorLoggingUser('Unable to determine login method right now. Please try again later.');
                setShowInvalidLoginPopup(true);
            } finally {
                setCheckingLoginMethod(false);
                requestAnimationFrame(() => setIsFading(false));
            }
        }, 300);
    };

    const backToIdentifier = (e) => {
        e?.preventDefault();
        setIsFading(true);
        setManualLoginMethod(null);
        setTimeout(() => {
            setLoginStep(1);
            requestAnimationFrame(() => setIsFading(false));
        }, 300);
    };

    const switchManualLoginMethod = (e) => {
        e?.preventDefault();
        let nextLogin = 'otp';
        switch(manualLoginMethod) {
            case 'password':
                nextLogin = 'otp';
                sendOtp(username);
                break;
            case 'passwordVerified':
                nextLogin = 'otpVerified';
                sendOtp(username);
                break;
            case 'otp' :
                nextLogin = 'password';
                break;
            case 'otpVerified':
                nextLogin = 'passwordVerified';
                break;
        }
        setManualLoginMethod(nextLogin);
    };

    const sendOtp = async (entry) => {
        setOtpError(null);
        setOtpSending(true);
        try {
            let body = {
                entry: entry
            };
            await userAuthService.sendLoginOTP(body);
            setOtpSent(true);
            setOtpResendCooldown(30);
        } catch(err) {
            setOtpError('Failed to send OTP. Please try again.');
        } finally {
            setOtpSending(false);
        }
    };

    useEffect(() => {
        if((manualLoginMethod === 'otp' || manualLoginMethod === 'otpVerified') && !otpSent && !otpSending && username) {
//            sendOtp(username);
        }
    }, [manualLoginMethod, otpSent, otpSending, username]);

    useEffect(() => {
        if(otpResendCooldown <= 0) return;
        const t = setTimeout(() => setOtpResendCooldown(c => c-1), 1000);
        return () => clearTimeout(t);
    }, [otpResendCooldown]);

    const handleResendOtp = (e) => {
        e?.preventDefault();
        if(otpResendCooldown > 0 || otpSending) return;
        setOtp('');
        sendOtp(username);
    };

    const handleAboutApiceTechClick = () => {
        window.location.href = 'https://apicetech.in';
    }

    const handleContactUsClick = () => {
        window.location.href = 'https://apicetech.in/contact-us';
    }

    return (
        <div className={className} style={{ cursor }}>
            {/* Background Elements */}
            <div className={className + '__background'}>
                <div className={className + '__background__gradient'}></div>
                <div className={className + '__background__pattern'}></div>
                <div className={className + '__background__shapes'}>
                    <div className={className + '__background__shape --shape1'}></div>
                    <div className={className + '__background__shape --shape2'}></div>
                    <div className={className + '__background__shape --shape3'}></div>
                </div>
            </div>

            {/* Main Container */}
            <div className={className + '__container'}>
                {/* Left Side - Branding */}
                <div className={className + '__branding'}>
                    <div className={className + '__branding__content'}>
                        <div className={className + '__branding__logo'}>
                            <KaizIQText className={className + '__branding__logoSvg'} />
                        </div>
                        <div className={className + '__branding__hero'}>
                            <h1 className={className + '__branding__title'}>
                                Engage, Nurture, Convert
                                <span className={className + '__branding__titleAccent'}> Leads Faster</span>
                            </h1>
                            <p className={className + '__branding__subtitle'}>
                                Run WhatsApp and Email campaigns, capture prospects, and track your pipeline — all in one place with KaizIQ Leads.
                            </p>
                        </div>
                        <div className={className + '__branding__features'}>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Bulk Messaging</strong>
                                    <span>Reach thousands via WhatsApp and Email with ease</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Lead Tracking</strong>
                                    <span>See every interaction, source, and status in one timeline</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Smart Automation</strong>
                                    <span>Drip campaigns, reminders, and follow-ups that convert</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login Form */}
                <div className={className + '__formSection'}>
                    <div className={className + '__formContainer' +
                        (preload ? ' ' + className + '__formContainer--preload' : '') +
                        (isFading ? ' ' + className + '__formContainer--fadeOut' : '')}>
                        {/* Mobile-only Logo (shown on phone screens) */}
                        <div className={className + '__mobileLogo'}>
                            <KaizIQText className={className + '__branding__logoSvg'} />
                        </div>

                        {/* Header */}
                        <div className={className + '__header'}>
                            <h2 className={className + '__welcomeTitle'}>Welcome Back!</h2>
                            <p className={className + '__welcomeSubtitle grey-text'}>
                                {loginStep === 1 ? 'Sign in to continue to KaizIQ' : 'Complete your authentication'}
                            </p>
                        </div>

                        {/* Step 1: Identifier Entry */}
                        {loginStep === 1 && (
                            <div className={className + '__step1'}>
                                {/* Google Login */}
                                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleFailure}
                                        useOneTap={false}
                                        text='continue_with'
                                        logo_alignment='center'
                                        shape='circle'
                                        size='medium'
                                        outline='none'/>
                                </GoogleOAuthProvider>

                                {googleErrors.length > 0 && (
                                    <div className={className + '__error'}>
                                        Google auth issues: {googleErrors.join(', ')}
                                    </div>
                                )}

                                {/* Divider */}
                                <div className={className + '__divider'}>
                                    <span className={className + '__dividerLine'}></span>
                                    <span className={className + '__dividerText grey-text sm-text'}>OR</span>
                                    <span className={className + '__dividerLine'}></span>
                                </div>

                                {/* Manual Login Form */}
                                <form onSubmit={handlePasswordCheck} className={className + '__form'}>
                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Email, Username or Phone</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <User className={className + '__inputIcon'} />
                                            <input
                                                type='text'
                                                className={className + '__input'}
                                                placeholder='Enter your email, username or phone'
                                                onChange={handleUserNameChange}
                                                disabled={cursor === 'wait'}
                                                value={username || ''}
                                            />
                                        </div>
                                        {phoneError && (
                                            <div className={className + '__fieldError'}>
                                                {phoneError}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={!username || username.trim() === '' || cursor === 'wait'}
                                    >
                                        {checkingLoginMethod ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Checking...
                                            </>
                                        ) : 'Continue'}
                                    </button>
                                </form>

                                {/* Footer Links */}
                                <div className={className + '__footer flex-row-center-1rem-gap sm-text'}>
                                    <div className={className + '__footer__t1 grey-text'}>
                                        Don't have an account?
                                    </div>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={signup}>
                                        Sign Up
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Password/OTP Entry */}
                        {loginStep === 2 && (
                            <div className={className + '__step2'}>
                                {/* User Summary */}
                                <div className={className + '__userSummary flex-row-center-space-between'}>
                                    <div className={className + '__userSummaryText sm-text'}>
                                        Signing in as <strong>{username}</strong>
                                    </div>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={backToIdentifier}>
                                        Not you?
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className={className + '__form'}>
                                    {/* Password Input */}
                                    {(manualLoginMethod === 'password' || manualLoginMethod === 'passwordVerified') && (
                                        <div className={className + '__inputGroup'}>
                                            <label className={className + '__label grey-text sm-text'}>Password</label>
                                            <div className={className + '__inputWrapper flex-row-center'}>
                                                <MdLock className={className + '__inputIcon'} />
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    className={className + '__input'}
                                                    placeholder='Enter your password'
                                                    onChange={handlePasswordChange}
                                                    disabled={cursor === 'wait'}
                                                />
                                                <IconButton
                                                    className={className + '__togglePassword'}
                                                    onClick={togglePasswordVisibility}
                                                    size="small"
                                                >
                                                    {showPassword ? <BiHide /> : <BiShow />}
                                                </IconButton>
                                            </div>
                                        </div>
                                    )}

                                    {/* OTP Input */}
                                    {(manualLoginMethod === 'otp' || manualLoginMethod === 'otpVerified') && (
                                        <div className={className + '__otpSection flex-column-center gap-1rem'}>
                                            <label className={className + '__label'}>Verification Code</label>
                                            <p className={className + '__otpDescription sm-text grey-text'}>
                                                Enter the 6-digit code sent to your email
                                                {(manualLoginMethod === "passwordVerified" || manualLoginMethod === "otpVerified") &&
                                                    ' and phone number'}
                                            </p>
                                            <div className={className + '__otpContainer'}>
                                                <OTPInput
                                                    value={otp}
                                                    onChange={setOtp}
                                                    autoFocus
                                                    OTPLength={6}
                                                    otpType='number'
                                                    disabled={otpSending || cursor === 'wait'}
                                                    inputClass={className + '__otpInput'}
                                                    className={className + '__otpWrapper'}
                                                />
                                            </div>
                                            <div className={className + '__otpFooter flex-row-center-0p5rem-gap'}>
                                                {otpSending && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>
                                                        <div className={className + '__spinner'}></div>
                                                        Sending code...
                                                    </span>
                                                )}
                                                {!otpSending && otpSent && otpResendCooldown > 0 && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>
                                                        Resend code in {otpResendCooldown}s
                                                    </span>
                                                )}
                                                {!otpSending && otpSent && otpResendCooldown === 0 && (
                                                    <button className={className + '__linkBtn sm-text hovered'} onClick={handleResendOtp}>
                                                        Resend code
                                                    </button>
                                                )}
                                                {!otpSent && !otpSending && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>Preparing verification...</span>
                                                )}
                                            </div>
                                            {otpError && (
                                                <div className={className + '__fieldError'}>
                                                    {otpError}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={cursor === 'wait' ||
                                            (manualLoginMethod === 'password' && (!password || invalidPassword)) ||
                                            (manualLoginMethod === 'otp' && (otp.length !== 6 || otpSending))}
                                    >
                                        {cursor === 'wait' ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Signing in...
                                            </>
                                        ) : 'Sign In'}
                                    </button>
                                </form>

                                {/* Alternative Login Method */}
                                <div className={className + '__alternativeLogin'}>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={switchManualLoginMethod}>
                                        {(manualLoginMethod === 'password' || manualLoginMethod === 'passwordVerified')
                                            ? 'Use verification code instead'
                                            : 'Use password instead'}
                                    </button>
                                </div>

                                {/* Footer Links */}
                                <div className={className + '__footer flex-row-center'}>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={forgotPassword}>
                                        Forgot password?
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {
//                        <div className={className + '__inner__bottom'}>
//                            <div className={className + '__inner__bottom__links flex-row-center-1vw-gap'}>
//                                <button className={className + '__linkBtn'} onClick={handleAboutApiceTechClick}>About APICE Tech</button>
//                                <button className={className + '__linkBtn'} onClick={handleContactUsClick}>Contact Us</button>
//                            </div>
//                        </div>
                    }
                </div>
            {/* Loading Overlay */}
            {checkingLoginMethod && (
                <div className={className + '__loadingOverlay'}>
                    <div className={className + '__loadingContent'}>
                        <div className={className + '__loadingSpinner'}></div>
                        <p className={className + '__loadingText'}>Checking login method...</p>
                    </div>
                </div>
            )}
            </div>
        </div>
    )
}

LoginPage.propTypes = {
    setToken: PropTypes.func.isRequired
};
